<script setup lang="ts">
import { computed, nextTick, ref, shallowRef, watch, type VNodeRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { VVirtualScroll } from 'vuetify/components'
import useTree, { type TreeItemNode } from '@/composables/useTree'
import { sortTree } from '@/app'
import { indexEbomTree, type EbomTreePart } from '@/utils/ebomTree'
import { openTreeRows, type TreeRow } from '@/utils/treeRows'
import useEbomTreeWidth, {
  DEFAULT_EBOM_TREE_WIDTH,
  MIN_EBOM_TREE_WIDTH,
} from '@/composables/useEbomTreeWidth'

const props = defineProps<{
  parts: EbomTreePart[]
  selectableIds: Set<string>
  selectedId: string | null
  hiddenArticleNumbers?: Set<string>
}>()

const emit = defineEmits<{
  select: [ebomPartId: string]
  'toggle-hidden': [articleNumber: string]
  'show-all': []
  focus: [ebomPartId: string]
}>()

const collapsed = defineModel<boolean>('collapsed', { default: false })

const { t } = useI18n()
const { treeRootNodes } = useTree()

type TreePart = Omit<EbomTreePart, 'parentId'> & { parentId?: string }
type EbomTreeNode = TreeItemNode<TreePart>

function sortNodes(nodes: EbomTreeNode[]): EbomTreeNode[] {
  return sortTree(
    nodes.map((n) => ({ ...n, children: sortNodes(n.children) })),
    (n) => n.children.length > 0,
    (n) => n.item.position || n.item.articleNumber,
  )
}

const rootNodes = computed(() =>
  sortNodes(treeRootNodes(props.parts.map((p) => ({ ...p, parentId: p.parentId ?? undefined })))),
)
const treeIndex = computed(() => indexEbomTree(props.parts))

const hiddenStates = computed(() => {
  const hidden = props.hiddenArticleNumbers
  const states = new Map<string, 'self' | 'assembly'>()
  if (!hidden?.size) return states
  const visit = (nodes: EbomTreeNode[], inHiddenAssembly: boolean) => {
    for (const n of nodes) {
      const self = hidden.has(n.item.articleNumber)
      if (self) states.set(n.item.id, 'self')
      else if (inHiddenAssembly) states.set(n.item.id, 'assembly')
      visit(n.children, inHiddenAssembly || self)
    }
  }
  visit(rootNodes.value, false)
  return states
})

function eyeTitle(node: EbomTreeNode): string {
  const state = hiddenStates.value.get(node.item.id)
  if (state === 'assembly') return t('component.bopModelViewer.hiddenWithAssembly')
  return state
    ? t('component.bopModelViewer.showArticle')
    : t('component.bopModelViewer.hideArticle')
}

function toggleHidden(node: EbomTreeNode) {
  if (hiddenStates.value.get(node.item.id) !== 'assembly') {
    emit('toggle-hidden', node.item.articleNumber)
  }
}

const openedIds = shallowRef(new Set<string>())
const rows = computed(() => openTreeRows(rootNodes.value, (n) => openedIds.value.has(n.item.id)))

const ENTER_STAGGER_MS = 12
const ENTER_STAGGER_MAX_MS = 120
const ENTER_DURATION_MS = 180
const LEAVE_DURATION_MS = 120

// Rows that just appeared, by their order of appearance, so they can cascade in.
const enteringOrder = shallowRef(new Map<string, number>())
let enterTimer: ReturnType<typeof setTimeout> | undefined
let animateNextRows = false

watch(rows, (next, previous) => {
  if (!animateNextRows) return
  animateNextRows = false
  const before = new Set(previous.map((r) => r.node.item.id))
  const order = new Map<string, number>()
  for (const r of next) if (!before.has(r.node.item.id)) order.set(r.node.item.id, order.size)
  enteringOrder.value = order
  clearTimeout(enterTimer)
  enterTimer = setTimeout(
    () => (enteringOrder.value = new Map()),
    ENTER_DURATION_MS + ENTER_STAGGER_MAX_MS,
  )
})

function enterDelay(id: string): string | undefined {
  const index = enteringOrder.value.get(id)
  if (index === undefined) return undefined
  return `${Math.min(index * ENTER_STAGGER_MS, ENTER_STAGGER_MAX_MS)}ms`
}

function openIds(ids: Iterable<string>) {
  animateNextRows = true
  openedIds.value = new Set([...openedIds.value, ...ids])
}

// Rows of a closing assembly fade out first and only then leave the list.
const leavingIds = shallowRef(new Set<string>())
const closingId = ref<string | null>(null)
let pendingClose: (() => void) | null = null
let leaveTimer: ReturnType<typeof setTimeout> | undefined

function finishPendingClose() {
  clearTimeout(leaveTimer)
  pendingClose?.()
}

function close(node: EbomTreeNode) {
  const index = rows.value.findIndex((r) => r.node.item.id === node.item.id)
  const depth = rows.value[index]?.depth ?? 0
  const leaving = new Set<string>()
  for (let i = index + 1; i < rows.value.length && rows.value[i].depth > depth; i++) {
    leaving.add(rows.value[i].node.item.id)
  }
  pendingClose = () => {
    pendingClose = null
    const ids = new Set(openedIds.value)
    ids.delete(node.item.id)
    openedIds.value = ids
    leavingIds.value = new Set()
    closingId.value = null
  }
  closingId.value = node.item.id
  leavingIds.value = leaving
  leaveTimer = setTimeout(pendingClose, LEAVE_DURATION_MS)
}

// A different 3D model brings a different structure: start again from its top level.
watch(
  () => rootNodes.value.map((n) => n.item.id).join(),
  () => {
    finishPendingClose()
    openedIds.value = new Set(rootNodes.value.map((n) => n.item.id))
  },
  { immediate: true },
)

function toggle(node: EbomTreeNode) {
  if (node.children.length === 0) return
  finishPendingClose()
  if (openedIds.value.has(node.item.id)) close(node)
  else openIds([node.item.id])
}

let selectedInTree = false
function activate(node: EbomTreeNode) {
  if (!props.selectableIds.has(node.item.id)) {
    toggle(node)
    return
  }
  selectedInTree = true
  emit('select', node.item.id)
}

function focusPart(node: EbomTreeNode) {
  if (props.selectableIds.has(node.item.id)) emit('focus', node.item.id)
}

const ROW_HEIGHT = 28
const LIST_PADDING = 4

const virtualScroll = ref<VVirtualScroll>()
function scrollToSelected() {
  // The virtual list only grows to its full height a frame after the rows changed.
  nextTick(() =>
    requestAnimationFrame(() => {
      const index = rows.value.findIndex((r) => r.node.item.id === props.selectedId)
      if (index >= 0) virtualScroll.value?.scrollToIndex(index)
    }),
  )
}

watch(
  () => props.selectedId,
  (id) => {
    const fromTree = selectedInTree
    selectedInTree = false
    if (!id) return
    const ancestors = treeIndex.value.ancestorIds(id)
    if (ancestors.some((a) => !openedIds.value.has(a))) {
      finishPendingClose()
      openIds(ancestors)
    }
    if (!fromTree && !collapsed.value) scrollToSelected()
  },
)

watch(collapsed, (isCollapsed) => {
  if (!isCollapsed && props.selectedId) scrollToSelected()
})

const { width } = useEbomTreeWidth()
const root = ref<HTMLDivElement>()
const resizing = ref(false)

function startResize(e: PointerEvent) {
  const handle = e.currentTarget as HTMLElement
  handle.setPointerCapture(e.pointerId)
  const startX = e.clientX
  const startWidth = width.value
  // Keep enough of the model uncovered to still see and click it.
  const maxWidth = (root.value?.parentElement?.clientWidth ?? Infinity) * 0.8
  resizing.value = true

  const move = (ev: PointerEvent) => {
    const next = startWidth + ev.clientX - startX
    width.value = Math.round(Math.min(maxWidth, Math.max(MIN_EBOM_TREE_WIDTH, next)))
  }
  const end = () => {
    resizing.value = false
    handle.removeEventListener('pointermove', move)
    handle.removeEventListener('pointerup', end)
    handle.removeEventListener('pointercancel', end)
  }
  handle.addEventListener('pointermove', move)
  handle.addEventListener('pointerup', end)
  handle.addEventListener('pointercancel', end)
}
</script>

<template>
  <div ref="root" class="viewer-ebom-tree">
    <transition name="viewer-ebom-tree-panel" mode="out-in">
      <v-btn
        v-if="collapsed"
        key="toggle"
        icon
        size="small"
        variant="tonal"
        color="grey-darken-2"
        :title="t('component.bopModelViewer.showEbomTree')"
        @click="collapsed = false"
      >
        <v-icon>account_tree</v-icon>
      </v-btn>
      <div v-else key="panel" class="viewer-ebom-tree__panel" :style="{ width: `${width}px` }">
        <div
          class="viewer-ebom-tree__resize"
          :class="{ 'viewer-ebom-tree__resize--active': resizing }"
          :title="t('component.bopModelViewer.resizeEbomTree')"
          @pointerdown.prevent="startResize"
          @dblclick="width = DEFAULT_EBOM_TREE_WIDTH"
        />
        <div class="viewer-ebom-tree__header">
          <v-icon size="16" color="grey-darken-2">account_tree</v-icon>
          <span class="viewer-ebom-tree__title">{{ t('component.bopModelViewer.ebomTree') }}</span>
          <v-btn
            v-if="hiddenArticleNumbers?.size"
            size="x-small"
            variant="text"
            color="primary"
            prepend-icon="visibility"
            class="ml-auto"
            @click="emit('show-all')"
          >
            {{ t('component.bopModelViewer.showAllArticles') }}
          </v-btn>
          <v-btn
            icon="chevron_left"
            size="x-small"
            variant="text"
            density="comfortable"
            :class="{ 'ml-auto': !hiddenArticleNumbers?.size }"
            :title="t('component.bopModelViewer.hideEbomTree')"
            @click="collapsed = true"
          />
        </div>
        <v-virtual-scroll
          ref="virtualScroll"
          class="viewer-ebom-tree__list"
          :style="{ height: `${rows.length * ROW_HEIGHT + 2 * LIST_PADDING}px` }"
          :items="rows"
          :item-key="(row: TreeRow<EbomTreeNode>) => row.node.item.id"
          :item-height="ROW_HEIGHT"
        >
          <template
            #default="{ item: row, itemRef }: { item: TreeRow<EbomTreeNode>; itemRef?: VNodeRef }"
          >
            <div
              :ref="itemRef"
              class="viewer-ebom-tree__row"
              :class="{
                'viewer-ebom-tree__row--selected': row.node.item.id === selectedId,
                'viewer-ebom-tree__row--unselectable': !selectableIds.has(row.node.item.id),
                'viewer-ebom-tree__row--entering': enteringOrder.has(row.node.item.id),
                'viewer-ebom-tree__row--leaving': leavingIds.has(row.node.item.id),
                'viewer-ebom-tree__row--hidden': hiddenStates.has(row.node.item.id),
              }"
              :style="{
                paddingInlineStart: `${row.depth * 12 + 4}px`,
                animationDelay: enterDelay(row.node.item.id),
              }"
              :title="
                selectableIds.has(row.node.item.id)
                  ? `${row.node.item.articleNumber} ${row.node.item.name ?? ''}`
                  : t('component.bopModelViewer.ebomTreeNotSelectable')
              "
              @click="activate(row.node)"
              @dblclick="focusPart(row.node)"
            >
              <span
                v-if="row.node.children.length > 0"
                class="viewer-ebom-tree__toggle"
                @click.stop="toggle(row.node)"
                @dblclick.stop
              >
                <v-icon
                  size="16"
                  class="viewer-ebom-tree__chevron"
                  :class="{
                    'viewer-ebom-tree__chevron--open':
                      openedIds.has(row.node.item.id) && closingId !== row.node.item.id,
                  }"
                >
                  chevron_right
                </v-icon>
              </span>
              <span v-else class="viewer-ebom-tree__toggle" />
              <span class="viewer-ebom-tree__number">{{ row.node.item.articleNumber }}</span>
              <span class="viewer-ebom-tree__name">{{ row.node.item.name }}</span>
              <span
                v-if="hiddenArticleNumbers"
                class="viewer-ebom-tree__eye"
                :class="{
                  'viewer-ebom-tree__eye--hidden': hiddenStates.has(row.node.item.id),
                  'viewer-ebom-tree__eye--inherited':
                    hiddenStates.get(row.node.item.id) === 'assembly',
                }"
                :title="eyeTitle(row.node)"
                @click.stop="toggleHidden(row.node)"
                @dblclick.stop
              >
                <v-icon size="16">
                  {{ hiddenStates.has(row.node.item.id) ? 'visibility_off' : 'visibility' }}
                </v-icon>
              </span>
            </div>
          </template>
        </v-virtual-scroll>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.viewer-ebom-tree {
  position: absolute;
  top: 8px;
  left: 8px;
  max-width: calc(100% - 16px);
  max-height: calc(100% - 64px);
  display: flex;
  flex-direction: column;
  z-index: 1;
  transition:
    top 150ms ease,
    max-height 150ms ease;
}

.viewer-ebom-tree__panel {
  position: relative;
  max-width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top left;
}

/* Just outside the edge, so it never competes with the list's scrollbar. */
.viewer-ebom-tree__resize {
  position: absolute;
  top: 0;
  right: -8px;
  bottom: 0;
  width: 8px;
  cursor: col-resize;
  touch-action: none;
  z-index: 1;
}

.viewer-ebom-tree__resize::after {
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 1px;
  width: 3px;
  border-radius: 2px;
  background: rgb(var(--v-theme-primary));
  opacity: 0;
  transition: opacity 120ms;
}

.viewer-ebom-tree__resize:hover::after,
.viewer-ebom-tree__resize--active::after {
  opacity: 0.6;
}

.viewer-ebom-tree-panel-enter-active {
  transition:
    opacity 220ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 220ms cubic-bezier(0.16, 1, 0.3, 1);
}

.viewer-ebom-tree-panel-leave-active {
  transition:
    opacity 140ms cubic-bezier(0.7, 0, 0.84, 0),
    transform 140ms cubic-bezier(0.7, 0, 0.84, 0);
}

.viewer-ebom-tree-panel-enter-from,
.viewer-ebom-tree-panel-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.viewer-ebom-tree__header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.viewer-ebom-tree__title {
  font-size: 0.8em;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.viewer-ebom-tree__list {
  flex: 0 1 auto;
  min-height: 0;
  padding: 4px 0;
  border-radius: 0 0 12px 12px;
  transition: height 180ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* The default scrollbar's arrow buttons crowd the list when the viewer is short. */
.viewer-ebom-tree__list::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.viewer-ebom-tree__list::-webkit-scrollbar-button {
  display: none;
}

.viewer-ebom-tree__list::-webkit-scrollbar-corner {
  background: transparent;
}

.viewer-ebom-tree__list::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.25);
}

.viewer-ebom-tree__list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

@supports not selector(::-webkit-scrollbar) {
  .viewer-ebom-tree__list {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
  }
}

.viewer-ebom-tree__row {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding-inline-end: 8px;
  font-size: 0.75em;
  cursor: pointer;
  white-space: nowrap;
}

.viewer-ebom-tree__row:hover {
  background: rgba(0, 0, 0, 0.05);
}

.viewer-ebom-tree__row--selected,
.viewer-ebom-tree__row--selected:hover {
  background: rgba(var(--v-theme-primary), 0.14);
}

.viewer-ebom-tree__row--selected .viewer-ebom-tree__number {
  color: rgb(var(--v-theme-primary));
}

.viewer-ebom-tree__row--unselectable {
  color: rgba(0, 0, 0, 0.45);
}

.viewer-ebom-tree__toggle {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
}

.viewer-ebom-tree__toggle:hover {
  background: rgba(0, 0, 0, 0.08);
}

.viewer-ebom-tree__chevron {
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
}

.viewer-ebom-tree__chevron--open {
  transform: rotate(90deg);
}

.viewer-ebom-tree__row--entering {
  animation: viewer-ebom-tree-row-in 180ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.viewer-ebom-tree__row--leaving {
  animation: viewer-ebom-tree-row-out 120ms cubic-bezier(0.7, 0, 0.84, 0) both;
  pointer-events: none;
}

@keyframes viewer-ebom-tree-row-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@keyframes viewer-ebom-tree-row-out {
  to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .viewer-ebom-tree-panel-enter-active,
  .viewer-ebom-tree-panel-leave-active,
  .viewer-ebom-tree__list,
  .viewer-ebom-tree__chevron {
    transition: none;
  }

  .viewer-ebom-tree__row--entering,
  .viewer-ebom-tree__row--leaving {
    animation: none;
  }
}

.viewer-ebom-tree__number {
  flex: none;
  font-weight: 600;
}

.viewer-ebom-tree__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: var(--v-medium-emphasis-opacity);
}

.viewer-ebom-tree__row--hidden .viewer-ebom-tree__number,
.viewer-ebom-tree__row--hidden .viewer-ebom-tree__name {
  opacity: 0.45;
}

.viewer-ebom-tree__eye {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-inline-start: auto;
  border-radius: 4px;
  color: rgba(0, 0, 0, 0.35);
  opacity: 0;
}

.viewer-ebom-tree__row:hover .viewer-ebom-tree__eye,
.viewer-ebom-tree__eye--hidden {
  opacity: 1;
}

.viewer-ebom-tree__eye:hover {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.75);
}

.viewer-ebom-tree__eye--hidden {
  color: rgba(0, 0, 0, 0.6);
}

.viewer-ebom-tree__eye--inherited,
.viewer-ebom-tree__eye--inherited:hover {
  background: none;
  color: rgba(0, 0, 0, 0.3);
  cursor: default;
}

@media (hover: none) {
  .viewer-ebom-tree__eye {
    opacity: 1;
  }
}
</style>
